import React, { ReactElement, useEffect, useState, FormEvent } from 'react';
import { Dropdown } from 'react-bootstrap';
import { Button, Form, FormControl, InputGroup } from 'react-bootstrap';
import { BiSearch } from 'react-icons/bi';
import { useNavigate } from 'react-router-dom';
import { Activite } from '../../../../../common/src/entities';
import { RECHERCHE_VITRINE_API, RequeteRechercheApi } from '../../../../../common/src/interfaces/rechercheApi';
import api from '../../../Utils/api';
import './BarreRechercheVitrine.scss';

const NB_RESULTATS = 10 as const;
const NB_CHAR_MIN_REQ = 3 as const;
const ID_BARRE_RECHERCHE = 'barre-recherche' as const;

interface PropsBarre {
    toggleEstPublic: boolean;
}

/**
 * Barre de recherche dans la vitrine permettant à l'usager de rechercher parmi
 * les activités.
 * @returns une barre de recherche
 */
function BarreRechercheVitrine(props: PropsBarre): ReactElement {
    const [valeurCherchee, setValeurCherchee] = useState<string>('');
    const [resultats, setResultats] = useState<Activite[]>([]);
    const [dropdownVisible, setDropdownVisible] = useState<boolean>(false);

    const navigate = useNavigate();

    /**
     * Reporte tout ce qui a été cliqué à l'écran.
     */
    useEffect(() => {
        window.addEventListener('click', gererEventClick);
        return () => window.removeEventListener('click', gererEventClick);
    }, []);

    /**
     * Après chaque lettre entrée par l'usager, faire une requete au serveur
     * avec l'api de recherche ou quand l'admin toggle la vue admin/publique
     */
    useEffect(() => {
        if (valeurCherchee.length < NB_CHAR_MIN_REQ) {
            setResultats([]);
            return;
        }

        const requete: RequeteRechercheApi = {
            requete: valeurCherchee.trim(),
            nombre: NB_RESULTATS,
            justePublique: props.toggleEstPublic,
        };

        api.get(RECHERCHE_VITRINE_API, { params: requete })
            .then((res) => res.data)
            .then((data) => setResultats(data))
            .catch((err) => console.error(err));
    }, [valeurCherchee]);

    /**
     * Permet de refermer le dropdown si on clique à l'extérieur du dropdown mais pas si
     * on reclique dans le champ de la barre de recherche. C'est une solution au Dropdown.Toggle
     * (dans react bootstrap) qui toggle le dropdown même quand on reclique dans le champ.
     * @param event ce que la souris a cliqué
     */
    function gererEventClick(event: MouseEvent): void {
        const target = event.target as HTMLElement;
        setDropdownVisible(target.id === ID_BARRE_RECHERCHE);
    }

    /**
     * Callback quand on appuie sur la loupe ou enter depuis la barre de recherche
     * preventDefault empêche d'ouvrir une nouvelle page.
     * @param e event triggered par le enter
     */
    function gererOnSubmit(e: FormEvent): void {
        e.preventDefault();

        if (valeurCherchee.length > 0) {
            navigate(`/recherche?requete=${valeurCherchee}`);
        } else if (valeurCherchee.length === 0) {
            navigate('/activites');
        }

        setDropdownVisible(false);
    }

    function gererOnClickItem(id: number) {
        navigate(`/activites/${id}`);
        setDropdownVisible(false);
    }

    /**
     * Callback d'une fonction map(). Pour chaque entite trouvé, afficher le nom et associer un
     * évenement clic.
     */
    function creerDropdownItem(activite: Activite): ReactElement {
        return (
            <Dropdown.Item key={activite.id} eventKey={`${activite.id}`} onClick={() => gererOnClickItem(activite.id)}>
                {activite.name}
            </Dropdown.Item>
        );
    }

    return (
        <Dropdown show={dropdownVisible}>
            <Form onSubmit={gererOnSubmit}>
                <InputGroup>
                    <FormControl
                        className="shadow-none"
                        type="text"
                        placeholder="Rechercher"
                        onChange={(e) => setValeurCherchee(e.target.value)}
                        value={valeurCherchee}
                        id={ID_BARRE_RECHERCHE}
                    />
                    <InputGroup.Append>
                        <Button type="submit" className="d-flex align-items-center rounded-right" aria-label="Rechercher">
                            <BiSearch size="1.25em" />
                        </Button>
                    </InputGroup.Append>
                    <Dropdown.Menu className="recherche-dropdown shadow mt-1">
                        {resultats.map(creerDropdownItem)}
                    </Dropdown.Menu>
                </InputGroup>
            </Form>
        </Dropdown>
    );
}

export default BarreRechercheVitrine;
